import { badgeVariants } from "@/shared/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

export type Initiative = { id: string; name: string };

/**
 * Initiatives (teams) cell for the properties table. A single chip-shaped button
 * shows the first initiative (truncated) plus a "+N" when there are more; clicking
 * it opens a popover listing every initiative in full. One control, both jobs —
 * stays single-line no matter how many initiatives or how long the names.
 */
export function PropertyInitiativesCell({ teams }: { teams?: Initiative[] }) {
  const initiatives = teams ?? [];

  if (initiatives.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const [first, ...rest] = initiatives;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={first.name}
          aria-label={
            rest.length > 0 ? `Show all ${initiatives.length} initiatives` : undefined
          }
          onClick={(e) => e.stopPropagation()}
          className={cn(
            badgeVariants({ variant: "secondary" }),
            "max-w-[240px] cursor-pointer gap-1.5 hover:bg-secondary/80",
          )}
        >
          <span className="min-w-0 truncate">{first.name}</span>
          {rest.length > 0 && (
            <span className="shrink-0 opacity-70">+{rest.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-3 shadow-lg ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Initiatives
        </p>
        <div className="flex flex-col gap-1.5">
          {initiatives.map((t) => (
            <span
              key={t.id}
              className="rounded-md bg-secondary px-2 py-1 text-sm text-secondary-foreground"
            >
              {t.name}
            </span>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
